import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);

  useEffect(() => {
    // Check for existing session
    const checkAuth = async () => {
      const storedUser = localStorage.getItem('crm_user');
      const accessToken = localStorage.getItem('access_token');

      // First try cookie-based session (SSO)
      try {
        const ssoMe = await authAPI.ssoMe();
        setUser(ssoMe.data);
        setLoading(false);
        return;
      } catch (e) {
        // ignore and fallback to JWT
      }

      if (storedUser && accessToken) {
        try {
          // Verify token is still valid
          const response = await authAPI.me();
          const userData = response.data;
          setUser({
            ...userData,
            accessToken: accessToken,
            refreshToken: localStorage.getItem('refresh_token')
          });
          // Check if password change is required
          setForcePasswordChange(userData.force_password_change || false);
        } catch (error) {
          // Token invalid, clear storage
          localStorage.removeItem('crm_user');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          setUser(null);
        }
      }

      setLoading(false);
    };
    
    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login(email, password);
      const { access_token, refresh_token, user: userData, force_password_change } = response.data;
      
      // Store tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      
      const userSession = {
        ...userData,
        accessToken: access_token,
        refreshToken: refresh_token
      };
      
      setUser(userSession);
      localStorage.setItem('crm_user', JSON.stringify(userSession));
      
      // Check if password change is required
      setForcePasswordChange(force_password_change || false);
      
      return { success: true, user: userSession, forcePasswordChange: force_password_change };
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Invalid credentials';
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      // Call backend to revoke sessions
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    }
    // Clear local state regardless of API result
    setUser(null);
    setForcePasswordChange(false);
    localStorage.removeItem('crm_user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  };

  const clearForcePasswordChange = () => {
    setForcePasswordChange(false);
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    
    const rolePermissions = {
      ADMIN: ['all'],
      SUB_ADMIN: ['leads', 'demos', 'deals', 'team', 'reports', 'incentives'],
      SALES_MANAGER: ['leads', 'demos', 'deals', 'team', 'reports', 'incentives'],
      EMPLOYEE: ['leads', 'demos', 'deals', 'my_incentives'],
      FINANCE_VIEWER: ['invoices', 'payouts', 'reports']
    };
    
    const permissions = rolePermissions[user.role] || [];
    return permissions.includes('all') || permissions.includes(permission);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasPermission,
    isAuthenticated: !!user,
    forcePasswordChange,
    clearForcePasswordChange
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
